import { useEffect, useRef, useState } from "react";
import { useRealtime } from "@/contexts/RealtimeContext";
import { api, apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Radio, LogIn, FileDown, Mail, UserPlus, UserX, Zap, DollarSign, Wrench, ClipboardList, Users } from "lucide-react";
import { toast } from "sonner";

const ACTION_META = {
  "board.login":          { icon: LogIn,        label: "Signed in",             tone: "text-emerald-300 bg-emerald-500/[0.06]" },
  "board.finance_export": { icon: FileDown,     label: "Exported finance CSV",  tone: "text-orange-300 bg-orange-500/[0.06]" },
  "board.packet.send":    { icon: Mail,         label: "Sent board packet",     tone: "text-indigo-300 bg-indigo-500/[0.06]" },
  "board.invite":         { icon: UserPlus,     label: "Invited director",      tone: "text-emerald-300 bg-emerald-500/[0.06]" },
  "board.revoke":         { icon: UserX,        label: "Revoked director",      tone: "text-red-300 bg-red-500/[0.06]" },
  "board.resend":         { icon: Mail,         label: "Resent invite",         tone: "text-zinc-300 bg-white/[0.04]" },
  "board.update":         { icon: UserPlus,     label: "Updated director",      tone: "text-zinc-300 bg-white/[0.04]" },
  "board.packet.email":   { icon: Mail,         label: "Emailed packet",        tone: "text-indigo-300 bg-indigo-500/[0.06]" },
  "board_digest.run_now": { icon: Mail,         label: "Fired board digest",    tone: "text-indigo-300 bg-indigo-500/[0.06]" },
  "board_digest.configure":{icon: Mail,         label: "Configured digest",     tone: "text-zinc-300 bg-white/[0.04]" },
  "impersonate.start":    { icon: Users,        label: "Impersonated resident", tone: "text-amber-300 bg-amber-500/[0.06]" },
  "impersonate.end":      { icon: Users,        label: "Ended impersonation",   tone: "text-amber-300 bg-amber-500/[0.06]" },
  "ledger.charge":        { icon: DollarSign,   label: "Posted a charge",       tone: "text-orange-300 bg-orange-500/[0.06]" },
  "payment.refund":       { icon: DollarSign,   label: "Refunded payment",      tone: "text-red-300 bg-red-500/[0.06]" },
  "ticket.create":        { icon: Wrench,       label: "Opened ticket",         tone: "text-zinc-300 bg-white/[0.04]" },
  "arc.request":          { icon: ClipboardList,label: "Filed ARC request",     tone: "text-zinc-300 bg-white/[0.04]" },
  "budget.upload":        { icon: FileDown,     label: "Uploaded budget",       tone: "text-emerald-300 bg-emerald-500/[0.06]" },
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.max(1, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
};

/**
 * ActivityTicker — real-time stream of admin actions on the dashboard.
 * Backfills the last 15 entries via /api/audit on mount, then subscribes to
 * the "admin_activity" websocket topic and prepends new rows as they arrive.
 */
export function ActivityTicker({ limit = 15 }) {
  const { subscribe, status } = useRealtime();
  const [items, setItems] = useState([]);
  const [pulse, setPulse] = useState(false);
  const seenIdsRef = useRef(new Set());

  // Initial backfill
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/audit", { params: { limit } });
        if (cancelled) return;
        const rows = data.items || [];
        rows.forEach((r) => seenIdsRef.current.add(r.id));
        setItems(rows);
      } catch (e) {
        // Silent — endpoint is admin-only; component won't render on non-admins
        if (e?.response?.status && e.response.status !== 403 && e.response.status !== 401) {
          toast.error(apiErrorMessage(e) || "Activity stream unavailable");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [limit]);

  // Live subscription
  useEffect(() => {
    const unsub = subscribe("admin_activity", ({ event, payload }) => {
      if (event !== "audit.new" || !payload?.id) return;
      if (seenIdsRef.current.has(payload.id)) return;
      seenIdsRef.current.add(payload.id);
      setItems((prev) => [payload, ...prev].slice(0, limit));
      setPulse(true);
      setTimeout(() => setPulse(false), 600);
    });
    return unsub;
  }, [subscribe, limit]);

  return (
    <Card className="p-4 border-white/10 bg-card overflow-hidden" data-testid="activity-ticker-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline flex items-center gap-2">
            Live activity
            <span
              className={"h-1.5 w-1.5 rounded-full transition-all " +
                (status === "online" ? "bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.6)]" : "bg-zinc-500")}
              data-testid={`activity-ticker-status-${status}`}
            />
          </div>
          <div className="text-sm font-semibold font-display mt-0.5 inline-flex items-center gap-2">
            <Radio className={"h-3.5 w-3.5 " + (pulse ? "text-orange-400 animate-pulse" : "text-zinc-500")} />
            <span>Admin & board actions ({items.length})</span>
          </div>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="rounded-md border border-dashed border-white/10 p-6 text-center text-xs text-zinc-500" data-testid="activity-ticker-empty">
          Nothing yet — new admin actions will stream in here as they happen.
        </div>
      ) : (
        <div className="space-y-1.5 max-h-72 overflow-y-auto scroll-slim">
          {items.map((e, idx) => {
            const meta = ACTION_META[e.action] || { icon: Zap, label: e.action, tone: "text-zinc-300 bg-white/[0.04]" };
            const Icon = meta.icon;
            const isFresh = idx === 0 && pulse;
            return (
              <div
                key={e.id || `${e.seq}-${e.at}`}
                className={
                  "flex items-start gap-2 rounded-md border px-2.5 py-2 text-xs transition " +
                  (isFresh ? "border-orange-500/40 bg-orange-500/[0.06]" : "border-white/5")
                }
                data-testid={`activity-ticker-row-${e.action}`}
              >
                <div className={"mt-0.5 h-6 w-6 shrink-0 rounded-md border border-white/10 flex items-center justify-center " + meta.tone}>
                  <Icon className="h-3 w-3" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-zinc-200 truncate">
                    <span className="font-medium">{e.actor_name || "System"}</span>
                    <span className="text-zinc-500 mx-1">·</span>
                    <span>{meta.label}</span>
                  </div>
                  <div className="text-[11px] text-zinc-500 truncate">
                    {e.actor_role || "system"} · {e.resource || ""}
                    {e.resource_id && e.resource_id !== "-" ? ` (${e.resource_id})` : ""}
                  </div>
                </div>
                <Badge variant="outline" className="border-white/10 text-zinc-400 text-[10px] tabular-nums h-5">
                  {timeAgo(e.at)}
                </Badge>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

export default ActivityTicker;
